import type { PlaceRef, RouteInput } from '@viaja/contracts';
import { Button, Chip, Icon } from '@viaja/design-system';
import { findPlace } from '@viaja/domain';
import { type FormEvent, useId, useState } from 'react';
import { formatRouteDates, tripKindDetail } from '../../lib/format';
import { useTrips } from '../../lib/queries';
import { TextField } from '../../ui/TextField';
import { Notice } from '../../ui/feedback';
import { DatesSheet, type DatesValue } from './DatesSheet';
import { PlaceSheet, toPlaceRef } from './PlaceSheet';

/**
 * Busca e cadastro de rota. Reproduz a marcação e as classes do `RouteSearch` do design system
 * (vc-search, vc-search-od, vc-field-wrap, vc-field, vc-swap, vc-search-chips) e acrescenta o que o
 * componente do DS não expõe: abrir os seletores de aeroporto e datas. Ver ADR-0003.
 * O chip "± 3 dias" do DS entra com o WP-03 (datas flexíveis).
 */
export interface RouteFormProps {
  initialTripId?: string | null;
  pending: boolean;
  serverError?: { message: string; fields: Record<string, string> } | null;
  onSubmit: (input: RouteInput) => void;
}

type Picker = 'origin' | 'destination' | 'dates' | null;

const DEFAULT_ORIGIN = toPlaceRef(findPlace('SAO')!);

export function RouteForm({ initialTripId = null, pending, serverError, onSubmit }: RouteFormProps) {
  const ids = { origin: useId(), destination: useId(), dates: useId() };
  const trips = useTrips();
  const [picker, setPicker] = useState<Picker>(null);
  const [origin, setOrigin] = useState<PlaceRef | null>(DEFAULT_ORIGIN);
  const [destination, setDestination] = useState<PlaceRef | null>(null);
  const [dates, setDates] = useState<DatesValue>({ outboundDate: '', returnDate: null, adults: 1 });
  const [groupAirports, setGroupAirports] = useState(true);
  const [directOnly, setDirectOnly] = useState(false);
  const [flexDays, setFlexDays] = useState(false);
  const [target, setTarget] = useState('');
  const [tripId, setTripId] = useState<string | null>(initialTripId);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fieldErrors = { ...errors, ...(serverError?.fields ?? {}) };
  const openTrips = (trips.data ?? []).filter((t) => t.status === 'planejando');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!origin) next.origin = 'Escolha de onde você sai.';
    if (!destination) next.destination = 'Escolha para onde quer ir.';
    if (origin && destination && origin.code === destination.code)
      next.destination = 'Origem e destino precisam ser diferentes.';
    if (!dates.outboundDate) next.outboundDate = 'Escolha as datas.';
    const targetPrice = target ? Number(target.replace(/\D/g, '')) : null;
    if (target && !(targetPrice && targetPrice > 0)) next.targetPrice = 'Digite um valor em reais.';
    setErrors(next);
    if (Object.keys(next).length || !origin || !destination) return;
    onSubmit({
      origin,
      destination,
      outboundDate: dates.outboundDate,
      returnDate: dates.returnDate,
      adults: dates.adults,
      groupAirports,
      directOnly,
      flexDays,
      targetPrice,
      alertEnabled: true,
      tripId,
    });
  };

  return (
    <form className="vc-search" onSubmit={submit} noValidate>
      <div className="vc-search-od">
        <PickField
          id={ids.origin}
          label="De onde"
          value={origin?.city ?? 'Escolha a origem'}
          detail={origin?.detail}
          error={fieldErrors.origin}
          onOpen={() => setPicker('origin')}
        />
        <button
          type="button"
          className="vc-swap"
          aria-label="Trocar origem e destino"
          onClick={() => {
            setOrigin(destination);
            setDestination(origin);
          }}
        >
          <Icon name="trocar" size={20} />
        </button>
        <PickField
          id={ids.destination}
          label="Para onde"
          value={destination?.city ?? 'Escolha o destino'}
          detail={destination?.detail}
          error={fieldErrors.destination}
          onOpen={() => setPicker('destination')}
        />
      </div>
      <PickField
        id={ids.dates}
        label="Quando"
        value={
          dates.outboundDate
            ? formatRouteDates(dates.outboundDate, dates.returnDate, false)
            : 'Escolha as datas'
        }
        detail={dates.outboundDate ? tripKindDetail(dates.returnDate, dates.adults, directOnly) : undefined}
        error={fieldErrors.outboundDate ?? fieldErrors.returnDate}
        onOpen={() => setPicker('dates')}
      />
      <div className="vc-search-chips">
        <Chip selected={flexDays} onClick={() => setFlexDays((v) => !v)}>
          ± 3 dias
        </Chip>
        <Chip selected={groupAirports} onClick={() => setGroupAirports((v) => !v)}>
          Todos os aeroportos
        </Chip>
        <Chip selected={directOnly} onClick={() => setDirectOnly((v) => !v)}>
          Só voos diretos
        </Chip>
      </div>

      <TextField
        label="Seu alvo"
        prefix="R$"
        inputMode="numeric"
        placeholder="Opcional"
        value={target}
        onChange={(e) => setTarget(e.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))}
        error={fieldErrors.targetPrice}
        hint="Quanto você quer pagar. Te aviso quando o preço chegar lá."
      />

      {openTrips.length > 0 ? (
        <div className="app-field">
          <span className="eyebrow vc-eyebrow">Viagem</span>
          <div className="app-wrap">
            <Chip selected={tripId === null} onClick={() => setTripId(null)}>
              Nenhuma
            </Chip>
            {openTrips.map((t) => (
              <Chip key={t.id} selected={tripId === t.id} icon="mala" onClick={() => setTripId(t.id)}>
                {t.name}
              </Chip>
            ))}
          </div>
        </div>
      ) : null}

      {serverError && Object.keys(serverError.fields).length === 0 ? (
        <Notice>{serverError.message}</Notice>
      ) : null}

      <div className="app-actionbar">
        <Button type="submit" variant="primary" icon="buscar" block disabled={pending}>
          {pending ? 'Buscando preços…' : 'Ver preços'}
        </Button>
      </div>

      <PlaceSheet
        open={picker === 'origin'}
        title="De onde"
        selected={origin?.code}
        onPick={(p) => {
          setOrigin(p);
          setPicker(null);
        }}
        onClose={() => setPicker(null)}
      />
      <PlaceSheet
        open={picker === 'destination'}
        title="Para onde"
        selected={destination?.code}
        onPick={(p) => {
          setDestination(p);
          setPicker(null);
        }}
        onClose={() => setPicker(null)}
      />
      <DatesSheet
        open={picker === 'dates'}
        value={dates}
        onConfirm={(v) => {
          setDates(v);
          setPicker(null);
        }}
        onClose={() => setPicker(null)}
      />
    </form>
  );
}

/** Campo do RouteSearch (mesma marcação do DS) que abre um seletor. */
function PickField({
  id,
  label,
  value,
  detail,
  error,
  onOpen,
}: {
  id: string;
  label: string;
  value: string;
  detail?: string | undefined;
  error?: string | undefined;
  onOpen: () => void;
}) {
  return (
    <div className="vc-field-wrap">
      <span id={`${id}-label`} className="eyebrow vc-eyebrow">
        {label}
      </span>
      <button
        type="button"
        className="vc-field app-pick"
        aria-labelledby={`${id}-label ${id}-value`}
        aria-describedby={error ? `${id}-err` : undefined}
        aria-haspopup="dialog"
        onClick={onOpen}
      >
        <span id={`${id}-value`} className="body-strong vc-field-value">
          {value}
        </span>
        {detail ? <span className="caption vc-field-detail">{detail}</span> : null}
      </button>
      {error ? (
        <span id={`${id}-err`} className="caption app-field-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
